import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useOutletContext, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { setChecklistData, setChecklistCardIds, setParallelData, setChecklistMetadata } from '../../../../store/card/card.action';
import { selectChecklistData, selectChecklistCardIds, selectParallelData, selectQueuedCardsDisplay, selectCollectionInvalidationKey, selectChecklistMetadata } from '../../../../store/card/card.selector';
import { selectUser } from '../../../../store/user/user.selector';
import { getChecklistCardIdsData, getChecklistDataByIdsData, getParallelAndMetadataData } from '../../../../utils/firebase/checklist';
import { fetchCollectionDataOnly } from '../../../../utils/firebase/collection';
import { normalizeForCompare } from '../../../../utils/textNormalization';

// styles
import {
    BodyContainer,
    HeaderContainer,
    SearchBarContainer,
    FilterBarContainer,
    LeftContainer,
    RightContainer,
    SearchFiltersPanel,
    QueuedCardsWrapper,
    ChecklistDisplayContainer,
} from './ChecklistDisplay.styles.js';

// components
import InputDropdown from '../../../../components/input-dropdown/InputDropdown.js';
import InputDropdownSearchable from '../../../../components/inputs/input-dropdown-searchable/InputDropdownSearchable.js';
import QueuedCards from '../../../../components/queued-cards/QueuedCards.js';
import LoadingIcon from '../../../../components/loading-icon/LoadingIcon.js';
import InputBar from '../../../../components/input-bar/InputBar.js';

function toTitleCase(str) {
    if (!str || typeof str !== 'string') return str;
    return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Normalize for accent-insensitive search: "José" and "Jose" both become "jose". */
function normalizeForSearch(str) {
    if (str == null || typeof str !== 'string') return '';
    return str
        .normalize('NFD')
        .replace(/\p{Mn}/gu, '')
        .toLowerCase();
}

export default function ChecklistDisplay() {
    const { sport, year, manufacturer, series, cardType, cardTypeCategory } = useParams();
    const dispatch = useDispatch();
    const outletContext = useOutletContext();
    const viewingUserID = outletContext?.viewingUserID ?? null;
    const user = useSelector(selectUser);
    const checklistData = useSelector(selectChecklistData);
    const checklistCardIds = useSelector(selectChecklistCardIds);
    const parallelData = useSelector(selectParallelData);
    const queuedCardsDisplay = useSelector(selectQueuedCardsDisplay);
    const collectionInvalidationKey = useSelector(selectCollectionInvalidationKey);
    const checklistMetadata = useSelector(selectChecklistMetadata);
    const [collectionData, setCollectionData] = useState({});
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [filterValue, setFilterValue] = useState('All');
    const [selectedTeam, setSelectedTeam] = useState('All');
    const [selectedParallel, setSelectedParallel] = useState('All');
    const [defaultParallel, setDefaultParallel] = useState('Base');
    const [isAddingToCollection, setIsAddingToCollection] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchValue(searchValue), 250);
        return () => clearTimeout(timer);
    }, [searchValue]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            const isErase = e.key === 'Backspace' || e.key === 'Delete';
            if (e.key.length !== 1 && !isErase) return;
            const active = document.activeElement;
            if (active && (
                active.tagName === 'INPUT' ||
                active.tagName === 'TEXTAREA' ||
                active.tagName === 'SELECT' ||
                active.isContentEditable
            )) return;
            const searchInput = document.getElementById('checklist-search-input');
            if (!searchInput) return;
            searchInput.focus();
            if (isErase) {
                /* Erase here instead of letting the key reach the input we just focused, so
                   exactly one character is removed and backspace can't navigate back. */
                e.preventDefault();
                setSearchValue((v) => v.slice(0, -1));
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const FILTER_OPTIONS = ['All', 'Collected', 'Not Collected'];

    const teamOptions = useMemo(() => {
        const teams = checklistMetadata?.teams;
        if (!teams || typeof teams !== 'object') return ['All'];
        return ['All', ...Object.keys(teams).sort()];
    }, [checklistMetadata]);

    const parallelOptions = useMemo(() => {
        if (!parallelData?.length) return ['Base'];
        const options = parallelData.map((p) =>
            typeof p === 'string' ? p : (p.name ?? p.text ?? '')
        ).filter(Boolean);
        const rest = options
            .filter((o) => String(o).toUpperCase() !== 'BASE')
            .map(toTitleCase);
        return ['Base', ...rest];
    }, [parallelData]);

    /* Print runs keyed by parallel name, so dropdowns can show "Gold Refractor /99" while
       still committing the bare name that collection data is keyed on. */
    const printRunByParallel = useMemo(() => {
        const map = new Map();
        (parallelData ?? []).forEach((p) => {
            if (typeof p === 'string' || !p) return;
            const name = p.name ?? p.text;
            if (name && p.limitedTo != null) map.set(normalizeForCompare(name), p.limitedTo);
        });
        return map;
    }, [parallelData]);

    const withPrintRun = useCallback((name) => {
        const printRun = printRunByParallel.get(normalizeForCompare(name));
        return printRun != null
            ? { value: name, label: `${name} /${printRun}` }
            : { value: name, label: name };
    }, [printRunByParallel]);

    const parallelLabeledOptions = useMemo(
        () => parallelOptions.map(withPrintRun),
        [parallelOptions, withPrintRun]
    );

    const parallelDropdownOptions = useMemo(
        () => ['All', ...parallelOptions].map(withPrintRun),
        [parallelOptions, withPrintRun]
    );

    const filteredCardIds = useMemo(() => {
        const q = normalizeForSearch(debouncedSearchValue ?? '');
        const qTrimmed = q.trim();
        const filterByCollection = filterValue !== 'All';
        const filterByTeam = selectedTeam !== 'All';
        const filterByParallel = selectedParallel !== 'All';

        return checklistCardIds.filter((cardId) => {
            const card = checklistData[cardId];
            if (!card) return false;

            if (qTrimmed) {
                const cardNumberNorm = normalizeForSearch(String(card.cardNumber ?? ''));
                const names = card.players ?? card.name ?? [];
                const nameList = Array.isArray(names) ? names : [names];
                const searchMatch = cardNumberNorm.includes(qTrimmed) || nameList.some((n) => n && normalizeForSearch(String(n)).includes(qTrimmed));
                if (!searchMatch) return false;
            }

            if (filterByCollection) {
                const collectedCount = (collectionData[cardId]?.collectedCards ?? []).length;
                const isCollected = collectedCount > 0;
                if (filterValue === 'Collected' && !isCollected) return false;
                if (filterValue === 'Not Collected' && isCollected) return false;
            }

            if (filterByTeam) {
                const cardTeams = card.team;
                if (!cardTeams || !Array.isArray(cardTeams)) return false;
                const selectedNorm = normalizeForCompare(selectedTeam);
                if (!cardTeams.some((t) => normalizeForCompare(t) === selectedNorm)) return false;
            }

            if (filterByParallel) {
                const selectedNorm = normalizeForCompare(selectedParallel);
                if (filterValue === 'Collected') {
                    const collectedCards = collectionData[cardId]?.collectedCards ?? [];
                    const collectedInSelectedParallel = collectedCards.some((c) => normalizeForCompare(c.cardParallelName) === selectedNorm);
                    if (!collectedInSelectedParallel) return false;
                } else {
                    // All or Not Collected: card must have this parallel in the set (all cards in category do, so e.g. Blue → all cards)
                    const raw = card.parallels ?? [];
                    const list = Array.isArray(raw) ? raw : [raw];
                    const cardParallelNames = list.map((p) => (typeof p === 'string' ? p : (p?.name ?? p?.text ?? ''))).filter(Boolean);
                    const hasParallel = cardParallelNames.length === 0 || cardParallelNames.some((name) => normalizeForCompare(name) === selectedNorm);
                    if (!hasParallel) return false;
                }
            }

            return true;
        });
    }, [checklistCardIds, checklistData, debouncedSearchValue, filterValue, collectionData, selectedTeam, selectedParallel]);

    const collectedParallelOrderMap = useMemo(() => {
        const map = {};
        parallelOptions.forEach((opt, i) => {
            map[String(opt).toUpperCase()] = i;
        });
        return map;
    }, [parallelOptions]);

    const sortCollectedByDropdownOrder = useCallback((collectedCards) => {
        if (!collectedCards?.length) return collectedCards;
        const order = (name) => collectedParallelOrderMap[String(name || '').toUpperCase()] ?? 999;
        return [...collectedCards].sort((a, b) =>
            order(a.cardParallelName) - order(b.cardParallelName)
        );
    }, [collectedParallelOrderMap]);

    const cardViewOutletContext = useMemo(() => ({
        filteredCardIds,
        checklistData,
        collectionData,
        parallelData,
        parallelOptions,
        sortCollectedByDropdownOrder,
        defaultParallel,
    }), [filteredCardIds, checklistData, collectionData, parallelData, parallelOptions, sortCollectedByDropdownOrder, defaultParallel]);

    const isCustomCategory = cardTypeCategory?.startsWith('custom:');

    const isLoading = !cardType || !cardTypeCategory;

    useEffect(() => {
        if (!sport || !year || !manufacturer || !series || !cardType || !cardTypeCategory) {
            return;
        }

        if (isCustomCategory) {
            dispatch(setChecklistData({}));
            dispatch(setChecklistCardIds([]));
            dispatch(setParallelData([]));
            return;
        }

        let cancelled = false;
        const checklistPath = `checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;
        const collectionPath = user?.uid
            ? `users/${user.uid}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`
            : null;

        const load = async () => {
            dispatch(setChecklistData({}));
            dispatch(setChecklistCardIds([]));
            dispatch(setParallelData([]));
            setCollectionData({});

            const [ids, { parallelData, metadata }, collData] = await Promise.all([
                getChecklistCardIdsData(checklistPath, { sortOrder: 'numeric' }),
                getParallelAndMetadataData(sport, year, manufacturer, series, cardType, cardTypeCategory),
                collectionPath ? fetchCollectionDataOnly(collectionPath) : Promise.resolve({}),
            ]);
            if (cancelled) return;
            dispatch(setChecklistCardIds(ids));
            dispatch(setParallelData(parallelData));
            dispatch(setChecklistMetadata(metadata));
            if (!cancelled && collData) setCollectionData(collData);

            if (ids.length === 0) return;

            const data = await getChecklistDataByIdsData(checklistPath, ids);
            if (cancelled) return;
            dispatch(setChecklistData(data));
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [sport, year, manufacturer, series, cardType, cardTypeCategory, isCustomCategory, user?.uid, dispatch]);

    useEffect(() => {
        if (!user?.uid || !sport || !year || !manufacturer || !series || !cardType || !cardTypeCategory || isCustomCategory) {
            return;
        }
        if (collectionInvalidationKey === 0) {
            return;
        }
        const collectionPath = `users/${user.uid}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;
        let cancelled = false;
        fetchCollectionDataOnly(collectionPath).then((data) => {
            if (!cancelled) setCollectionData(data);
        });
        return () => { cancelled = true; };
    }, [collectionInvalidationKey, user?.uid, sport, year, manufacturer, series, cardType, cardTypeCategory, isCustomCategory]);

    if (isLoading) {
        return (
            <ChecklistDisplayContainer>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
                    <LoadingIcon />
                </div>
            </ChecklistDisplayContainer>
        );
    }

    if (isCustomCategory) {
        return (
            <ChecklistDisplayContainer>
                <div style={{ color: 'rgba(255,255,255,0.7)', padding: '24px' }}>
                    Custom category support coming soon.
                </div>
            </ChecklistDisplayContainer>
        );
    }

    const hasCards = checklistCardIds.length > 0;
    const hasData = checklistCardIds.every((id) => checklistData[id]);

    if (!hasCards) {
        return (
            <ChecklistDisplayContainer>
                <div style={{ color: 'rgba(255,255,255,0.7)', padding: '24px' }}>
                    No cards in this category.
                </div>
            </ChecklistDisplayContainer>
        );
    }

    if (!hasData) {
        return (
            <ChecklistDisplayContainer>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
                    <LoadingIcon />
                </div>
            </ChecklistDisplayContainer>
        );
    }

    const hasQueue = queuedCardsDisplay.length !== 0 && !viewingUserID;

    const searchFiltersContent = !isAddingToCollection && (
        <>
            <SearchBarContainer>
                <InputBar
                    id="checklist-search-input"
                    label="Search"
                    placeholder="Search card by name or number..."
                    value={searchValue}
                    updateValue={setSearchValue}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            setSearchValue('');
                            e.target.blur();
                        }
                    }}
                />
            </SearchBarContainer>
            <FilterBarContainer>
                <InputDropdown id="checklist-filter-dropdown" label="Filter" options={FILTER_OPTIONS} value={filterValue} onChange={(newValue) => setFilterValue(newValue)} />
                <InputDropdownSearchable id="checklist-parallel-dropdown" label="Parallel" options={parallelDropdownOptions} value={selectedParallel} onChange={(newValue) => setSelectedParallel(newValue)} searchPlaceholder="Search parallels..." portalPanel />
                <InputDropdown id="checklist-team-dropdown" label="Team" options={teamOptions} value={selectedTeam} onChange={(newValue) => setSelectedTeam(newValue)} />
                <InputDropdownSearchable id="checklist-default-parallel-dropdown" label="Default Parallel" options={parallelLabeledOptions} value={defaultParallel} onChange={(newValue) => setDefaultParallel(newValue)} searchPlaceholder="Search parallels..." portalPanel />
            </FilterBarContainer>
        </>
    );

    return (
        <BodyContainer>
            {/* No queue: full-width sticky header above the checklist */}
            {!hasQueue && (
                <HeaderContainer>
                    {searchFiltersContent}
                </HeaderContainer>
            )}
            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                <LeftContainer>
                    <Outlet context={cardViewOutletContext} />
                </LeftContainer>
                {/* Queue present: right column sticks as a unit — search/filters on top, queue below */}
                {hasQueue && (
                    <RightContainer>
                        <SearchFiltersPanel>
                            {searchFiltersContent}
                        </SearchFiltersPanel>
                        <QueuedCardsWrapper>
                            <QueuedCards onAddingToCollectionChange={setIsAddingToCollection} />
                        </QueuedCardsWrapper>
                    </RightContainer>
                )}
            </div>
        </BodyContainer>
    );
}
